// views/Dashboard/PainelExecutivo/sections/ceo/useCeoDados.ts
//
// Dados da aba CEO, um hook por bloco: cada bloco aparece quando o SEU dado
// chega. As interrupções levam alguns segundos (a RPC roda em cima da
// telemetria) e não podem segurar o dinheiro do mês, que já está no cache do
// Financeiro.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../../../src/lib/supabase';
import { api } from '../../../../../src/services/api';
import { montarUsinas } from '../../../../../src/lib/dashboard/painelCeo';
import type {
  DiaGeracao, EpisodioParado, LinhaVsMeta, MetaFaturamentoLinha, RateioUgLinha, UnidadeGeradoraLinha, UsinaCeo,
} from '../../../../../src/lib/dashboard/painelCeo';
import type { KpiDepartamento } from '../../../../../src/lib/dashboard/kpisDepartamento';
import { casarRegistros, contaComoPerda, getMotivo } from '../../../../Geradoras/interrupcoes';
import { buscarCatalogo, indexarCatalogo, tituloAlarme } from '../../../../../src/lib/alarmesGeradoras';
import { numerosChamados } from '../../../../../src/lib/dashboard/kpisAutomaticos';
import type { NumerosChamados } from '../../../../../src/lib/dashboard/kpisAutomaticos';
import type { GeradoraSemReportar } from '../../../../../types';

const FATOR_PADRAO = 4.5;

/** Mensagem de erro do Supabase/PostgREST como a pessoa precisa ler. */
const mensagem = (e: any, padrao: string): string => e?.message || e?.details || padrao;

/** O mês corrente, em hora local: 1º dia 00h até o 1º dia do mês seguinte. */
export function useMesCorrente() {
  return useMemo(() => {
    const agora = new Date();
    return {
      agora,
      inicio: new Date(agora.getFullYear(), agora.getMonth(), 1),
      fim: new Date(agora.getFullYear(), agora.getMonth() + 1, 1),
      ano: agora.getFullYear(),
      mes: agora.getMonth() + 1,
    };
  }, []);
}

/** As usinas da Vigor (rateio + telemetria) e o fator kWh/kWp/dia de Config. */
export function useUsinasCeo() {
  const [usinas, setUsinas] = useState<UsinaCeo[]>([]);
  const [fator, setFator] = useState(FATOR_PADRAO);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    Promise.all([
      supabase
        .from('rateio_unidades_geradoras')
        .select('id, nome, ativo, capacidade_kwp, telemetria_site_id, numero_uc_geradora, numero_uc_geradora_antigo'),
      supabase.from('unidades_geradoras').select('site_id_api, nome_usina, capacidade_instalada'),
      supabase.from('system_settings').select('value').eq('key', 'geradoras_fator_geracao_kwh_por_kwp_dia').maybeSingle(),
    ])
      .then(([rateio, unidades, setting]) => {
        if (!vivo) return;
        if (rateio.error) throw rateio.error;
        if (unidades.error) throw unidades.error;
        setUsinas(montarUsinas((rateio.data || []) as RateioUgLinha[], (unidades.data || []) as UnidadeGeradoraLinha[]));
        const salvo = Number(setting.data?.value);
        setFator(Number.isFinite(salvo) && salvo > 0 ? salvo : FATOR_PADRAO);
      })
      .catch((e) => vivo && setErro(mensagem(e, 'Não foi possível carregar as usinas.')))
      .finally(() => vivo && setLoading(false));
    return () => { vivo = false; };
  }, []);

  return { usinas, fator, loading, erro };
}

/** Meses de história da geração: o mês corrente e os 11 anteriores. */
export const MESES_HISTORIA_GERACAO = 12;

/**
 * Geração x meta, por usina e mês, nos últimos 12 meses (o mês corrente vem com
 * a meta rateada até hoje), e quem está parado agora. Uma consulta só alimenta
 * o "este mês", o "mês a mês" e a energia do mês fechado.
 */
export function useGeracaoCeo(usinas: UsinaCeo[], inicio: Date, fim: Date) {
  const [linhas, setLinhas] = useState<LinhaVsMeta[]>([]);
  const [diarios, setDiarios] = useState<DiaGeracao[]>([]);
  const [paradasAgora, setParadasAgora] = useState<GeradoraSemReportar[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const sites = useMemo(() => usinas.map((u) => u.siteId).filter((s): s is string => !!s), [usinas]);
  const chave = sites.join(',');

  useEffect(() => {
    if (sites.length === 0) return;
    let vivo = true;
    setLoading(true);
    const desde = new Date(inicio.getFullYear(), inicio.getMonth() - (MESES_HISTORIA_GERACAO - 1), 1);
    // Os últimos 7 dias COMPLETOS dão o ritmo da projeção do mês (hoje ainda não acabou).
    const hoje0 = new Date(); hoje0.setHours(0, 0, 0, 0);
    const seteDias = new Date(hoje0.getTime() - 7 * 86_400_000);
    Promise.all([
      api.geradoras.getGeracaoVsMeta(sites, desde, fim),
      api.geradoras.getSerieGeracao(sites, seteDias, hoje0, 'dia').catch(() => []),
      // Falha aqui não derruba o bloco: some só a linha do "agora".
      api.geradoras.getUsinasParadas().catch(() => [] as GeradoraSemReportar[]),
    ])
      .then(([vsMeta, dias, paradas]) => {
        if (!vivo) return;
        setLinhas(vsMeta);
        setDiarios(dias.map((d) => ({ site_id_api: d.site_id_api, bucket: d.bucket, geracao_kwh: d.geracao_kwh })));
        setParadasAgora(paradas.filter((p) => sites.includes(p.site_id_api)));
        setErro(null);
      })
      .catch((e) => vivo && setErro(mensagem(e, 'Não foi possível carregar a geração do mês.')))
      .finally(() => vivo && setLoading(false));
    return () => { vivo = false; };
    // `chave` resume `sites`: a lista nova com os mesmos sites não refaz a consulta.
  }, [chave, inicio, fim]);

  return { linhas, diarios, paradasAgora, loading: loading && linhas.length === 0, erro };
}

/**
 * Paradas do mês que CONTAM como perda, com o motivo registrado.
 *
 * Mesma régua da aba Análise de Geradoras (`casarRegistros` + `contaComoPerda`):
 * falta de comunicação não é parada, e o episódio que alguém classificou como
 * "não é falha" sai da conta. Episódio "a confirmar" (sem dado ainda para saber
 * se a usina gerou) também fica de fora — costuma ser só o coletor atrasado.
 */
export function useParadasCeo(usinas: UsinaCeo[], inicio: Date, agora: Date) {
  const [episodios, setEpisodios] = useState<EpisodioParado[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const temUsinas = usinas.length > 0;

  useEffect(() => {
    if (!temUsinas) return;
    let vivo = true;
    Promise.all([
      api.geradoras.getInterrupcoes(inicio, agora),
      api.geradoras.getRegistrosInterrupcao(inicio, agora),
      // O catálogo traduz o código do alarme ("GridV.OutLim") em português. Sem ele, a tela
      // ainda funciona: mostra o código cru.
      api.geradoras.getCatalogoAlarmes().catch(() => []),
    ])
      .then(([detectadas, registros, catalogo]) => {
        if (!vivo) return;
        const indice = indexarCatalogo(catalogo);
        const linhas = casarRegistros(detectadas, registros, { inicio, fim: agora });
        setEpisodios(
          linhas
            .filter((l) => l.detectada && !l.detectada.a_confirmar && contaComoPerda(l))
            .map((l) => ({
              siteId: l.site_id_api,
              inicio: l.detectada!.inicio,
              fim: l.detectada!.fim,
              horas: l.detectada!.horas_equivalentes_periodo ?? 0,
              motivo: l.registro ? getMotivo(l.registro.motivo)?.rotulo ?? null : null,
              energiaKwh: l.detectada!.energia_nao_gerada_kwh_periodo ?? 0,
              tipo: l.detectada!.tipo === 'parcial' ? 'parcial' as const : 'parada' as const,
              emAberto: l.detectada!.em_aberto,
              alarmes: [...new Set(l.detectada!.alarmes.map((a) => tituloAlarme(a, buscarCatalogo(indice, a))))],
              pctReferencia: l.detectada!.pct_da_referencia,
            })),
        );
        setErro(null);
      })
      .catch((e) => vivo && setErro(mensagem(e, 'Não foi possível carregar as paradas do mês.')))
      .finally(() => vivo && setLoading(false));
    return () => { vivo = false; };
  }, [temUsinas, inicio, agora]);

  return { episodios, loading, erro };
}

/** Metas de faturamento por usina — leitura e gravação. */
export function useMetasFaturamento() {
  const [metas, setMetas] = useState<MetaFaturamentoLinha[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const { data, error } = await supabase
      .from('geradoras_metas_faturamento')
      .select('rateio_unidade_geradora_id, vigente_desde, meta_brl');
    if (error) {
      setErro(mensagem(error, 'Não foi possível carregar as metas de faturamento.'));
    } else {
      setMetas((data || []).map((m: any) => ({ ...m, meta_brl: Number(m.meta_brl) || 0 })));
      setErro(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  /**
   * Grava a meta de cada usina a partir de `vigenteDesde` (1º dia do mês).
   * Upsert por (usina, mês): salvar de novo no mesmo mês corrige, não duplica.
   */
  const salvar = useCallback(async (vigenteDesde: string, valores: { usinaId: string; meta: number }[]) => {
    if (valores.length === 0) return;
    const { error } = await supabase.from('geradoras_metas_faturamento').upsert(
      valores.map((v) => ({
        rateio_unidade_geradora_id: v.usinaId,
        vigente_desde: vigenteDesde,
        meta_brl: v.meta,
        atualizado_em: new Date().toISOString(),
      })),
      { onConflict: 'rateio_unidade_geradora_id,vigente_desde' },
    );
    if (error) throw new Error(mensagem(error, 'Não foi possível salvar as metas.'));
    await carregar();
  }, [carregar]);

  return { metas, loading, erro, salvar };
}

export type NovoKpi = Pick<KpiDepartamento, 'departamento' | 'nome' | 'como_medir' | 'unidade' | 'sentido' | 'meta' | 'valor_atual'>
  & Partial<Pick<KpiDepartamento, 'calculo_automatico' | 'status'>>;

/** KPIs dos departamentos + o nome de quem propôs. */
export function useKpisDepartamento() {
  const [kpis, setKpis] = useState<KpiDepartamento[]>([]);
  const [nomes, setNomes] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const { data, error } = await supabase.from('kpis_departamento').select('*').order('proposto_em');
    if (error) {
      setErro(mensagem(error, 'Não foi possível carregar os KPIs.'));
      setLoading(false);
      return;
    }
    const lista = (data || []).map((k: any) => ({
      ...k,
      meta: Number(k.meta),
      valor_atual: k.valor_atual == null ? null : Number(k.valor_atual),
    })) as KpiDepartamento[];
    setKpis(lista);
    setErro(null);
    setLoading(false);

    const ids = [...new Set(lista.flatMap((k) => [k.proposto_por, k.validado_por]).filter((x): x is string => !!x))];
    if (ids.length === 0) return;
    const { data: perfis } = await supabase.from('profiles').select('id, full_name').in('id', ids);
    setNomes(new Map((perfis || []).map((p: any) => [String(p.id), String(p.full_name || '').split(' ')[0]])));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const executar = useCallback(async (operacao: PromiseLike<{ error: any }>, padrao: string) => {
    const { error } = await operacao;
    if (error) throw new Error(mensagem(error, padrao));
    await carregar();
  }, [carregar]);

  const propor = useCallback(
    (novo: NovoKpi) => executar(supabase.from('kpis_departamento').insert(novo), 'Não foi possível propor o KPI.'),
    [executar],
  );
  /** Vários de uma vez, num insert só: o "validar todas" e a troca (nova + a recusada). */
  const proporVarios = useCallback(
    (novos: NovoKpi[]) => executar(supabase.from('kpis_departamento').insert(novos), 'Não foi possível salvar os KPIs.'),
    [executar],
  );
  const atualizar = useCallback(
    (id: number, campos: Partial<KpiDepartamento>) =>
      executar(supabase.from('kpis_departamento').update(campos).eq('id', id), 'Não foi possível salvar o KPI.'),
    [executar],
  );
  const apagar = useCallback(
    (id: number) => executar(supabase.from('kpis_departamento').delete().eq('id', id), 'Não foi possível apagar o KPI.'),
    [executar],
  );

  return { kpis, nomes, loading, erro, propor, proporVarios, atualizar, apagar };
}

/**
 * Chamados da Central de Solicitações para Tecnologia: resolvidos e tempo no mês
 * corrente e no anterior, e a fila parada há mais de 7 dias. Alimenta os KPIs de
 * Tecnologia — a fonte é a mesma lista que o time de TI trabalha.
 */
export function useChamadosTi(agora: Date) {
  const [numeros, setNumeros] = useState<{ atual: NumerosChamados; anterior: NumerosChamados } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    const desde = new Date(agora.getFullYear(), agora.getMonth() - 1, 1).toISOString();
    supabase
      .from('tarefas')
      .select('status, created_at, data_conclusao')
      .eq('departamento_solicitacao', 'Tecnologia')
      .not('tipo_solicitacao', 'is', null)
      // Rejeitada também ganha data_conclusao e entrava como chamado resolvido (#6353).
      .or('solicitacao_status.is.null,solicitacao_status.neq.rejeitado')
      // Só o que foi concluído no mês corrente ou no anterior entra nas contas.
      .gte('data_conclusao', desde)
      .limit(1000)
      .then(({ data, error }) => {
        if (!vivo) return;
        if (error) { setErro(mensagem(error, 'Não foi possível carregar os chamados.')); return; }
        const ord = agora.getFullYear() * 12 + agora.getMonth();
        const lista = (data || []) as { status: string | null; created_at: string; data_conclusao: string | null }[];
        setNumeros({ atual: numerosChamados(lista, ord), anterior: numerosChamados(lista, ord - 1) });
      });
    return () => { vivo = false; };
  }, [agora]);

  return { numeros, erro };
}
